import { AILevel } from '../ai/types';
import { SurvivalStats } from '../types/SurvivalSaveData';
import { MONSTER_DATABASE, ABILITY_UP_VALUES, NORMAL_MONSTER_IDS } from './monsters';

/** サバイバル対戦相手プール（隠しキャラを除く通常キャラのみ） */
export const SURVIVAL_ALL_MONSTER_IDS: string[] = NORMAL_MONSTER_IDS;

/** ステータス上限 */
export const STAT_CAP = 999;

/** 1勝あたりのボーナスポイント */
export const SURVIVAL_BONUS_POINTS_PER_WIN = 3;

/** ボーナスポイント1ptあたりのHP上昇量 */
export const SURVIVAL_BONUS_HP_PER_POINT = 3;

/** 勝利数ベースのAIレベルテーブル */
const AI_LEVEL_TABLE: Array<{ minWins: number; level: AILevel }> = [
  { minWins: 24, level: AILevel.LV6 }, // 達人: 真の挑戦
  { minWins: 16, level: AILevel.LV5 },
  { minWins: 12, level: AILevel.LV4 },
  { minWins: 8, level: AILevel.LV3 },
  { minWins: 4, level: AILevel.LV2 },
  { minWins: 0, level: AILevel.LV1 },
];

/**
 * 勝利数から現在のAIレベルを返す
 */
export function getSurvivalAiLevel(winCount: number): AILevel {
  for (const entry of AI_LEVEL_TABLE) {
    if (winCount >= entry.minWins) return entry.level;
  }
  return AILevel.LV1;
}

/**
 * 勝利数から敵の成長ステージ数を返す（均一: 4勝ごとに1段階、上限なし）
 */
export function getSurvivalEnemyStage(winCount: number): number {
  return Math.floor(winCount / 4);
}

/**
 * 勝利数からフェーズ倍率を返す
 * フェーズ1（0〜11勝）: 1.3 - プレイヤー有利
 * フェーズ2（12〜19勝）: 0.8 - CPU追い上げ
 * フェーズ3（20勝〜）: 0.6 - CPU優勢
 */
export function getSurvivalPhaseMultiplier(winCount: number): number {
  if (winCount < 12) return 1.3;
  if (winCount < 20) return 0.8;
  return 0.6;
}

/**
 * 1勝分の自動成長量を計算する
 * 各ステータス: floor(abilityUp × phaseMultiplier / 4)
 */
export function calcAutoGrowth(monsterId: string, winCount: number): SurvivalStats {
  const abilityUp = ABILITY_UP_VALUES[monsterId];
  if (!abilityUp) {
    return { hp: 0, strength: 0, special: 0, speed: 0, toughness: 0 };
  }
  const multiplier = getSurvivalPhaseMultiplier(winCount);
  return {
    hp: Math.floor(abilityUp.hp * multiplier / 4),
    strength: Math.floor(abilityUp.strength * multiplier / 4),
    special: Math.floor(abilityUp.special * multiplier / 4),
    speed: Math.floor(abilityUp.speed * multiplier / 4),
    toughness: Math.floor(abilityUp.toughness * multiplier / 4),
  };
}

/**
 * ボーナスポイント1ptをステータスに適用する（イミュータブル）
 * HP: +SURVIVAL_BONUS_HP_PER_POINT, 他: +1
 * 999を超えない
 */
export function applyBonusPoint(
  stats: SurvivalStats,
  statKey: keyof SurvivalStats,
): SurvivalStats {
  const gain = statKey === 'hp' ? SURVIVAL_BONUS_HP_PER_POINT : 1;
  const newVal = Math.min(stats[statKey] + gain, STAT_CAP);
  return { ...stats, [statKey]: newVal };
}

/**
 * Fisher-Yatesシャッフル
 */
function shuffle<T>(arr: T[]): T[] {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * 対戦相手キューを生成・継続する
 * - existingQueue が残っている場合はそのまま返す
 * - 空（または未指定）の場合は excludeId を除いた7体をシャッフルして返す
 */
export function createShuffledOpponentQueue(
  excludeId: string,
  existingQueue?: string[],
): string[] {
  if (existingQueue && existingQueue.length > 0) return existingQueue;
  const pool = SURVIVAL_ALL_MONSTER_IDS.filter((id) => id !== excludeId);
  return shuffle(pool);
}
